import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminShell from "@/components/layout/AdminShell";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminLedger from "@/pages/admin/AdminLedger";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPartnerDetail from "@/pages/admin/AdminPartnerDetail";
import AdminMigrations from "@/pages/admin/AdminMigrations";
import MigrationBoard from "@/pages/admin/MigrationBoard";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminTicketDetail from "@/pages/admin/AdminTicketDetail";
import AdminInboundInbox from "@/pages/admin/AdminInboundInbox";
import AdminCannedResponses from "@/pages/admin/AdminCannedResponses";
import AdminInternetPlans from "@/pages/admin/AdminInternetPlans";
import AdminLegalDocs from "@/pages/admin/AdminLegalDocs";
import AdminInternetSignups from "@/pages/admin/AdminInternetSignups";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminBackups from "@/pages/admin/AdminBackups";
import SyncHealth from "@/pages/admin/SyncHealth";
import EnvironmentBanner from "@/components/EnvironmentBanner";
import InternetSignup from "@/pages/InternetSignup";
import PartnerShell from "@/components/layout/PartnerShell";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerPayoutDetail from "@/pages/partner/PartnerPayoutDetail";
import PartnerTickets from "@/pages/partner/PartnerTickets";
import PartnerTicketDetail from "@/pages/partner/PartnerTicketDetail";
import PartnerFeedback from "@/pages/partner/PartnerFeedback";
import PartnerCustomers from "@/pages/partner/PartnerCustomers";
import PartnerTeam from "@/pages/partner/PartnerTeam";
import PartnerPriceList from "@/pages/partner/PartnerPriceList";
import PublicSignup from "@/pages/PublicSignup";
import { useParams } from "react-router-dom";

function LegacyMigrationRedirect() {
  const { partnerCode } = useParams();
  return <Navigate to={`/admin/onboarding/${partnerCode}`} replace />;
}

function RoleGate({ role, children }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <div className="text-zinc-500 text-sm font-medium">Loading…</div>
      </div>
    );
  }
  if (!state) return <Navigate to="/login" replace state={{ from: location }} />;
  // Accept multiple roles when `role` is an array. `partner` gate also
  // accepts `partner_staff` (v1.10.28 — staff logins under a partner
  // that can't see money) so both roles reach /portal correctly.
  const allowed = Array.isArray(role) ? role : (role === "partner" ? ["partner", "partner_staff"] : [role]);
  if (role && !allowed.includes(state.user.role)) {
    return <Navigate to={state.user.role === "admin" ? "/admin" : "/portal"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { state } = useAuth();
  if (state === null) return null;
  if (!state) return <Landing />;
  return <Navigate to={state.user.role === "admin" ? "/admin" : "/portal"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <EnvironmentBanner />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* /signup/:slug is the short partner-referral URL that customers actually
              paste in messaging apps. It renders the full NBN signup wizard scoped
              to that partner_code — same component as /internet/signup, just a
              cleaner URL. */}
          <Route path="/signup/:slug" element={<InternetSignup />} />
          <Route path="/signup/:slug/:flow" element={<InternetSignup />} />
          <Route path="/internet/signup" element={<InternetSignup />} />
          <Route path="/internet/signup/:partnerCode" element={<InternetSignup />} />
          <Route path="/internet/signup/:partnerCode/:flow" element={<InternetSignup />} />

          <Route path="/admin" element={<RoleGate role="admin"><AdminShell /></RoleGate>}>
            <Route index element={<AdminOverview />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="partners/:code" element={<AdminPartnerDetail />} />
            <Route path="ledger" element={<AdminLedger />} />
            <Route path="onboarding" element={<AdminMigrations />} />
            <Route path="sync-health" element={<SyncHealth />} />
            <Route path="onboarding/:partnerCode" element={<MigrationBoard />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="tickets/:id" element={<AdminTicketDetail />} />
            <Route path="canned-responses" element={<AdminCannedResponses />} />
            <Route path="inbound-inbox" element={<AdminInboundInbox />} />
            {/* /admin/handover-inbox retired v1.10.60 — the paste-and-parse
                surface was replaced by direct NBN-credential entry on the
                customer drawer. Redirect any stale bookmarks to Customers. */}
            <Route path="handover-inbox" element={<Navigate to="/admin/customers" replace />} />
            {/* Legacy /admin/migrations URLs — keep working for bookmarks / open tabs. */}
            <Route path="migrations" element={<Navigate to="/admin/onboarding" replace />} />
            <Route path="migrations/:partnerCode" element={<LegacyMigrationRedirect />} />
            <Route path="internet-plans" element={<AdminInternetPlans />} />
            <Route path="legal-docs" element={<AdminLegalDocs />} />
            <Route path="signups" element={<AdminInternetSignups />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="backups" element={<AdminBackups />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="/portal" element={<RoleGate role="partner"><PartnerShell /></RoleGate>}>
            <Route index element={<PartnerDashboard />} />
            <Route path="payouts/:payoutId" element={<PartnerPayoutDetail />} />
            <Route path="onboarding" element={<MigrationBoard isPartnerView={true} />} />
            <Route path="tickets" element={<PartnerTickets />} />
            <Route path="tickets/:id" element={<PartnerTicketDetail />} />
            <Route path="feedback" element={<PartnerFeedback />} />
            <Route path="customers" element={<PartnerCustomers />} />
            <Route path="team" element={<PartnerTeam />} />
            <Route path="price-list" element={<PartnerPriceList />} />
            {/* Legacy /portal/migration — keep working. */}
            <Route path="migration" element={<Navigate to="/portal/onboarding" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster
          position="top-right"
          theme="dark"
          toastOptions={{
            className: "font-sans",
            style: { borderRadius: 12, border: "1px solid rgb(30 41 59)", background: "rgb(15 23 42)", color: "rgb(226 232 240)" },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
